/**
 * Shared admin shell — breadcrumbs + warning banner + horizontal nav.
 *
 * Replaces the inline AdminHeader pattern (banner only) used previously.
 * Every admin page wraps its body in <AdminShell>; the shell figures
 * out the active section from the URL and renders the right breadcrumbs.
 *
 * Detail-page crumbs (e.g. "Admin > Orgs > Acme Corp"): pages pass the
 * `detail` prop to override the leaf — without it, the leaf is the
 * section label.
 */
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, LayoutDashboard, Cpu, Database, BookOpen, Building2, Users, ScrollText, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AdminShellProps {
  /** Optional subtitle. Currently unused — kept on the prop signature so
   *  call sites don't need editing if we want to reintroduce a banner. */
  subtitle?: string;
  /** Optional detail-page leaf crumb. e.g. "Acme Corp" on /admin/orgs/:id. */
  detail?: string;
  children: React.ReactNode;
}

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Match by `startsWith` to highlight the section across detail routes. */
  match: string;
}

// Nav item order is intentional — Dashboard first, then platform-config
// (Providers, SCF), then tenant-data (Orgs, Users), then governance
// (Audit log) at the tail.
const NAV: NavItem[] = [
  { label: 'Dashboard',         href: '/admin',                   icon: LayoutDashboard, match: '/admin' },
  { label: 'Providers',         href: '/admin/providers',         icon: Cpu,             match: '/admin/providers' },
  { label: 'SCF Dataset',       href: '/admin/scf',               icon: Database,        match: '/admin/scf' },
  { label: 'Security Controls', href: '/admin/security-controls', icon: BookOpen,        match: '/admin/security-controls' },
  { label: 'All Orgs',          href: '/admin/orgs',              icon: Building2,       match: '/admin/orgs' },
  { label: 'All Users',         href: '/admin/users',             icon: Users,           match: '/admin/users' },
  { label: 'Audit Logs',        href: '/admin/audit-logs',        icon: ScrollText,      match: '/admin/audit-logs' },
];

export function AdminShell({ subtitle: _subtitle, detail, children }: AdminShellProps) {
  void _subtitle;  // accepted for back-compat; banner is no longer rendered.
  const { pathname } = useLocation();

  // Resolve the section the user is currently in. Dashboard is the
  // exact-match fallback so /admin/* doesn't always light up Dashboard.
  const section =
    NAV.slice(1).find((n) => pathname.startsWith(n.match)) ??
    (pathname === '/admin' ? NAV[0] : NAV[0]);

  // Breadcrumbs: Admin Home > Section [> Detail].
  const crumbs: Array<{ label: string; href?: string }> = [{ label: 'Admin Home', href: '/admin' }];
  if (section.href !== '/admin') crumbs.push({ label: section.label, href: detail ? section.href : undefined });
  if (detail) crumbs.push({ label: detail });

  return (
    <div className="space-y-5">
      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-muted-foreground">
        {crumbs.map((c, i) => (
          <div key={i} className="inline-flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/60" />}
            {c.href ? (
              <Link to={c.href} className="hover:text-foreground transition-colors">
                {c.label}
              </Link>
            ) : (
              <span className="text-foreground font-medium">{c.label}</span>
            )}
          </div>
        ))}
      </nav>

      {/* Administration title — matches the org-level pattern of having
          a top-of-page header below the breadcrumbs. */}
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-red-500/10 flex items-center justify-center shrink-0">
          <ShieldAlert className="h-5 w-5 text-red-600 dark:text-red-400" />
        </div>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-foreground">Administration</h1>
          <p className="text-sm text-muted-foreground">Platform admin mode — cross-tenant visibility. Every action is recorded in the audit log.</p>
        </div>
      </div>

      {/* Horizontal nav — pinned at the top so admin sections are always
          one click away regardless of which page you're on. */}
      <div className="border-b border-border overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max">
          {NAV.map((n) => {
            const active = n.href === '/admin' ? pathname === '/admin' : pathname.startsWith(n.match);
            const Icon = n.icon;
            return (
              <Link
                key={n.href}
                to={n.href}
                className={cn(
                  'inline-flex items-center gap-2 px-3 py-2.5 -mb-px border-b-2 text-sm font-medium transition-colors whitespace-nowrap',
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
                )}
              >
                <Icon className="h-4 w-4" />
                {n.label}
              </Link>
            );
          })}
        </div>
      </div>

      {children}
    </div>
  );
}
